import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import { ProfileIcon } from "@icons";
import { ActivityFilterPanel } from "@components/activityTimeline/filters/ActivityFilterPanel";
import {
    ACTIVITY_FILTER_TAB_KEYS,
    type ActivityFilterTab,
} from "@components/activityTimeline/filters/activityFilters.types";
import { ORGANIZATION_OBJECT_FIELD_OPTIONS } from "@components/history/components/historyObjectFieldFilterOptions";
import { useHistoryChangeFilterTabs } from "@components/history/timeline/historyFilterTabs";
import { useOrganizationActorFilterRows } from "./components/OrganizationActorFilterCombobox";
import type { OrganizationActivitySelection } from "./organizationActivityFilters.types";

interface OrganizationFilterTabsParams {
    selection: OrganizationActivitySelection;
    pickableSeries: readonly ActivitySeries[];
    pickableOutcomes: readonly ActivityOutcomeKind[];
    /** The page's Clients filter; narrows the listed client users. */
    clientWorkspaceIds: string[] | undefined;
    isChannelFilterEnabled: boolean;
    onChange: (selection: OrganizationActivitySelection) => void;
}

interface UpdatedByPanelProps {
    actorIds: number[] | undefined;
    series: ActivitySeries[] | undefined;
    pickableSeries: readonly ActivitySeries[];
    clientWorkspaceIds: string[] | undefined;
    onChange: (selection: OrganizationActivitySelection) => void;
}

/** Owns its search, so the rows list in full each time the tab opens. */
const UpdatedByPanel = (props: UpdatedByPanelProps) => (
    <ActivityFilterPanel
        rows={useOrganizationActorFilterRows(props)}
        emptyLabel="No matching people"
    />
);

/** The firm Activity page's Filter popover tabs: its own Updated by, then the workspace page's tabs. */
export function useOrganizationFilterTabs({
    selection,
    pickableSeries,
    pickableOutcomes,
    clientWorkspaceIds,
    isChannelFilterEnabled,
    onChange,
}: OrganizationFilterTabsParams): ActivityFilterTab[] {
    const actorParams: UpdatedByPanelProps = {
        actorIds: selection.actorIds,
        series: selection.series,
        pickableSeries,
        clientWorkspaceIds,
        onChange,
    };
    const actorRows = useOrganizationActorFilterRows(actorParams);
    const changeTabs = useHistoryChangeFilterTabs({
        filters: selection,
        onChange,
        isChannelFilterEnabled,
        outcomes: pickableOutcomes,
        objectOptions: ORGANIZATION_OBJECT_FIELD_OPTIONS,
    });

    return [
        {
            key: ACTIVITY_FILTER_TAB_KEYS.UPDATED_BY,
            label: "Updated by",
            icon: <ProfileIcon />,
            chipLabel: actorRows.chipLabel,
            onClear: actorRows.onClear,
            panel: <UpdatedByPanel {...actorParams} />,
        },
        ...changeTabs,
    ];
}
